import { BaseToolbarNavPageLayout } from '../../shared/views/baseLayoutViews';
import { ExportRecurringProjectLocationSubmissions, CompletionRate } from './models';
import { ActiveLocationRecurringFormProjectTypeahead } from './ActiveLocationRecurringFormProjectTypeAhead/views';
import { DateRangeSelector } from './DateRangeSelector/views';
import { addMinutes, format, endOfDay, startOfDay, subDays } from 'date-fns';
import { ProjectCompletionRate } from './ProjectCompletionRate/views';
import { RecipientEmailSelector } from './RecipientEmailSelector/views';
import { EmailSelectorLegendModal } from './EmailSelectorLegendModal/views';
import {
    showLoadingDialog,
    hideLoadingDialog,
    showSuccessMessage,
    showErrorMessage
} from '../../../shared/utils/notifications';

const DEFAULT_DATE_RANGE = 30;

export const ComplianceProjectCompletionRateReport = BaseToolbarNavPageLayout.extend({
    template: '#tpl_project_compliance_report',
    allow_reverse_transitions: true,
    toolbarnav_show_add_task: false,
    toolbarnav_title: function () {
        return _t('mobile_compliance_submissions_report');
    },

    // This is a Parent/Recurring Project, we use one model to represent all the Project Types.
    parentProject: null, // Project

    recipientEmail: null, // string, default value is null as it defaults to users email on back-end
    dateStart: null, // datetime
    dateEnd: null, // datetime
    dateRange: null, // int

    regions: {
        r_info_selector: '.r_info_selector',
        r_info_completion_rate: '.r_info_completion_rate',
        r_info_email_selector: '.r_info_email_selector',
        r_info_date_range_selector: '.r_info_date_range_selector'
    },

    events: {
        'click .submit_report': 'exportReport'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        const currentDate = new Date();
        const currentUTCDate = addMinutes(currentDate, currentDate.getTimezoneOffset());

        this.setDateRange(DEFAULT_DATE_RANGE);
        this.dateEnd = format(endOfDay(currentUTCDate), $f.utils.UTC_ISO_FORMAT);

        this.selectParentProject = this.selectParentProject.bind(this);
        this.handleInvalidRecipientEmail = this.handleInvalidRecipientEmail.bind(this);
        this.handleValidRecipientEmail = this.handleValidRecipientEmail.bind(this);
        this.setRecipientEmailAddress = this.setRecipientEmailAddress.bind(this);
        this.saveDateRange = this.saveDateRange.bind(this);
        this.showSendEmailToLegend = this.showSendEmailToLegend.bind(this);
    },

    getDateStart: function (dateRange) {
        const currentDate = new Date();
        const currentUTCDate = addMinutes(currentDate, currentDate.getTimezoneOffset());
        const pastDate = this.getNDaysAgo(currentUTCDate, dateRange);
        return format(pastDate, $f.utils.UTC_ISO_FORMAT);
    },

    getNDaysAgo: function (date, numberOfDays) {
        // This function is inclusive of the current day. If date is 2022-10-31 and numberOfDays is 30
        // we want to return 2022-10-02 instead of 2022-10-01.
        return subDays(startOfDay(date), numberOfDays - 1);
    },

    onRender: function () {
        this.renderSelector();
        const submitButton = this.$('.submit_report');

        if (this.parentProject) {
            submitButton.removeClass('hidden');
            this.renderDateRangeSelector();
            this.renderCompletionRate();
            this.renderEmailSelector();
        }
    },

    exportReport: function (event) {
        event.preventDefault();
        showLoadingDialog();

        const location = $f.mobile.dashboard.get_active_account_or_group();

        const filters = {
            recipient_email: this.recipientEmail,
            date_start: this.dateStart,
            date_end: this.dateEnd
        };

        const exportSubmissions = new ExportRecurringProjectLocationSubmissions({
            location_id: location.get('id'),
            parent_project_id: this.parentProject.get('id'),
            filters
        });

        exportSubmissions.fetch({
            success: function (response) {
                hideLoadingDialog();
                showSuccessMessage();
            },
            error: function (response) {
                hideLoadingDialog();
                showErrorMessage();
            }
        });
    },

    renderSelector: function () {
        this.r_info_selector.show(
            new ActiveLocationRecurringFormProjectTypeahead({
                model: this.parentProject,
                post_on_select: this.selectParentProject
            })
        );
    },

    selectParentProject: function (parentProject) {
        this.parentProject = parentProject;
        this.setDateRange(DEFAULT_DATE_RANGE);
        this.renderSelector();
        this.renderDateRangeSelector();
        this.renderCompletionRate();
        this.renderEmailSelector();

        const submitButton = this.$('.submit_report');
        submitButton.removeClass('hidden');
        submitButton.prop('disabled', false);
        submitButton.removeClass('disabled');
    },

    renderDateRangeSelector: function () {
        this.r_info_date_range_selector.show(
            new DateRangeSelector({
                title: _t('mobile_compliance_time_frame'),
                saveDateRange: this.saveDateRange
            })
        );
    },

    renderCompletionRate: function () {
        const location = $f.mobile.dashboard.get_active_account_or_group();
        const locationId = location.get('id');

        const filters = {
            parent_project_id: this.parentProject.get('id'),
            date_start: this.dateStart,
            date_end: this.dateEnd
        };

        const completionRate = new CompletionRate({ location_id: locationId, filters });
        completionRate.fetch({
            success: () => {
                this.r_info_completion_rate.show(
                    new ProjectCompletionRate({
                        model: completionRate,
                        dateRange: this.dateRange
                    })
                );
            },
            error: () => {
                this.r_info_completion_rate.show(
                    new ProjectCompletionRate({
                        model: completionRate,
                        dateRange: this.dateRange
                    })
                );
            }
        });
    },

    renderEmailSelector: function () {
        const location = $f.mobile.dashboard.get_active_account_or_group();
        const user = $f.active_user;

        this.r_info_email_selector.show(
            new RecipientEmailSelector({
                title: _t('mobile_compliance_send_submissions_to'),
                userEmail: user.get('email'),
                locationEmail: location.get('email'),
                saveEmailAddress: this.setRecipientEmailAddress,
                handleValidEmail: this.handleValidRecipientEmail,
                handleInvalidEmail: this.handleInvalidRecipientEmail,
                handleShowLegend: this.showSendEmailToLegend
            })
        );
    },

    setRecipientEmailAddress: function (emailAddress) {
        this.recipientEmail = emailAddress;
    },

    setDateRange: function (dateRange) {
        this.dateRange = this.parseDateRange(dateRange);
        this.dateStart = this.getDateStart(this.dateRange);
    },

    saveDateRange: function (selectedDateRange) {
        this.setDateRange(selectedDateRange);

        this.renderCompletionRate();
    },

    parseDateRange: function (dateRange) {
        const parsedDateRange = parseInt(dateRange);

        if (isNaN(parsedDateRange)) {
            return DEFAULT_DATE_RANGE;
        }
        return parsedDateRange;
    },

    handleValidRecipientEmail: function () {
        const submitButton = this.$('.submit_report');
        submitButton.prop('disabled', false);
        submitButton.removeClass('disabled');
    },

    handleInvalidRecipientEmail: function () {
        const submitButton = this.$('.submit_report');
        submitButton.prop('disabled', true);
        submitButton.addClass('disabled');
    },

    showSendEmailToLegend: function (event) {
        $f.application.content_overlay.show(
            new EmailSelectorLegendModal({
                title: _t('mobile_compliance_send_submissions_to_legend_title'),
                description: _t('mobile_compliance_send_submissions_to_legend_description_days', {
                    days: this.dateRange
                })
            })
        );

        event.preventDefault();
    }
});
